import { useState } from 'react'
import { useNavigate } from 'react-router'
import { Star, RefreshCw } from 'lucide-react'
import { usePropertyReviews } from '@/hooks/useApi'
import { useToast } from '@/hooks/use-toast'
import { api } from '@/api/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { ChannelMark } from '@/components/channel-icons'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { ErrorState } from '@/components/ui/error-state'
import { EmptyState } from '@/components/ui/empty-state'
import { formatDate, getChannelLabel, getChannelBadgeVariant } from '@/lib/format-utils'

interface Review {
  platform?: string | null
  external_id?: string | null
  guest_name?: string | null
  rating?: number | null
  text?: string | null
  check_in?: string | null
  check_out?: string | null
  received_at?: string | null
  reservation_id?: string | null
}

interface SyncReviewsResult {
  skipped?: boolean
  error?: string | null
  reviewsFound?: number
  reviewsStored?: number
  reviewsMatched?: number
}

interface ReviewsTabProps {
  propertyId: string
  isActive: boolean
}

/**
 * Reviews tab for the property detail page. Lists every guest review received
 * for the property (both platforms), newest first. Clicking a row opens the
 * linked reservation so an operator can cross-check the review against the stay.
 */
export function ReviewsTab({ propertyId, isActive }: ReviewsTabProps) {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [isSyncing, setIsSyncing] = useState(false)
  const { data, isLoading, error, refetch } = usePropertyReviews(propertyId, {
    enabled: !!propertyId && isActive,
  })

  const reviews: Review[] = data?.reviews || []

  const handleSyncReviews = async () => {
    setIsSyncing(true)
    try {
      const result: SyncReviewsResult = await api.syncPropertyAirbnbReviews(propertyId)
      if (result.skipped) {
        toast({
          title: 'Nothing to sync',
          description: 'This property has no Airbnb mapping.',
        })
      } else if (result.error) {
        toast({
          title: 'Sync failed',
          description: result.error,
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'Sync completed',
          description: `Found ${result.reviewsFound || 0}, stored ${result.reviewsStored || 0}, matched ${result.reviewsMatched || 0}`,
        })
      }
      await refetch()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : undefined
      toast({
        title: 'Sync failed',
        description: message || 'Failed to sync Airbnb reviews',
        variant: 'destructive',
      })
    } finally {
      setIsSyncing(false)
    }
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Reviews</CardTitle>
          <CardDescription>Guest reviews received for this property.</CardDescription>
        </CardHeader>
        <CardContent>
          <LoadingSkeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Reviews</CardTitle>
          <CardDescription>Guest reviews received for this property.</CardDescription>
        </CardHeader>
        <CardContent>
          <ErrorState error={error} onRetry={refetch} />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Reviews</CardTitle>
            <CardDescription>
              Guest reviews received for this property, newest first. Click a review to open its
              reservation.
            </CardDescription>
          </div>
          <Button onClick={handleSyncReviews} disabled={isSyncing} variant="secondary" size="sm">
            {isSyncing ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Sync Airbnb reviews
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {reviews.length === 0 ? (
          <div className="p-6">
            <EmptyState message="No reviews received yet" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Guest</TableHead>
                  <TableHead>Platform</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>Review</TableHead>
                  <TableHead>Stay</TableHead>
                  <TableHead>Received</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reviews.map((review, index) => (
                  <TableRow
                    key={`${review.platform}:${review.external_id ?? index}`}
                    className={review.reservation_id ? 'cursor-pointer hover:bg-surface2' : ''}
                    onClick={() => {
                      if (review.reservation_id) {
                        navigate(`/reservations/${review.reservation_id}`)
                      }
                    }}
                  >
                    <TableCell className="font-medium">{review.guest_name || '—'}</TableCell>
                    <TableCell>
                      <Badge
                        variant={getChannelBadgeVariant(review.platform) as 'default' | 'secondary'}
                        className="gap-1 text-xs"
                      >
                        <ChannelMark channel={review.platform} className="h-3.5 w-3.5" />
                        {getChannelLabel(review.platform)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {review.rating != null ? (
                        <span className="inline-flex items-center gap-1 text-sm">
                          <Star className="h-3.5 w-3.5 fill-current" />
                          {review.rating}/5
                        </span>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className="max-w-md truncate">{review.text || '—'}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      {review.check_in && review.check_out
                        ? `${formatDate(review.check_in)} → ${formatDate(review.check_out)}`
                        : '—'}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {formatDate(review.received_at ?? '')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
